import React from 'react';
import { Instagram, Plus } from 'lucide-react';
import './LandingPage.css';

const PreptuneRedesigned = () => {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [expandedFaq, setExpandedFaq] = React.useState(null);

  const tools = [
    {
      title: "Podcast Tool",
      description: "Morning briefings with local news, traffic update, and your schedule. Customized for you and instantly ready.",
      image: "/image/intro5.png"
    },
    {
      title: "Journal Tool",
      description: "Talk about your day naturally. Preptune writes it down for you. Track your mood and lock entries for complete privacy.",
      image: "/image/intro15.png"
    },
    {
      title: "Study Tool",
      description: "Adapts to how you learn best. Explains things simply and helps you understand faster.",
      image: "/image/intro4.png"
    },
    {
      title: "Scheduling Tool",
      description: "Say it or type it and events get added in seconds. Preptune organizes your day automatically.",
      image: "/image/intro18.png"
    }
  ];

  const faqs = [
    {
      question: "Who is Preptune for?",
      answer: "People who forget things easily and want a smarter way to stay on top of life. Just chat naturally with your voice or type, and Preptune keeps track. It sends you gentle reminders about what's important so you never miss a beat."
    },
    {
      question: "Is Preptune free to use?",
      answer: "Yes! Preptune is 100% free. No fees, no subscriptions, nothing. All features are completely free to use."
    },
    {
      question: "Does Preptune remember things long-term?",
      answer: "Yes! Preptune remembers your conversations using persistent memory, what matters to you, and builds on past chats. You choose what it remembers and can delete anything anytime."
    },
    {
      question: "How do I control what Preptune remembers?",
      answer: "You're in full control. Delete individual memories, clear entire conversations, or lock things away privately. What Preptune knows is always up to you."
    },
    {
      question: "Is my information private and secure?",
      answer: "Absolutely. Your conversations are encrypted. Locked journal entries? We can't see them, only you can. You control everything and can delete it whenever you want."
    },
    {
      question: "What devices can I use Preptune on?",
      answer: "Preptune works all devices such as laptops, ipads and mobile. Our service is free to use. Try it out now!"
    },
    {
      question: "What makes Preptune different?",
      answer: "Preptune keeps track of what matters to you based on what you share. It comes with tools like scheduling, journaling, and studying that make your day easier. Not just another chat app."
    },
  ];

  React.useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.preptune-header');
      if (window.scrollY > 20) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % tools.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [tools.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % tools.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + tools.length) % tools.length);
  };

  const getSlideStyle = (index) => {
    const diff = index - activeSlide;
    const normalizedDiff = diff > 2 ? diff - tools.length : diff < -2 ? diff + tools.length : diff;
    
    if (normalizedDiff === 0) {
      return {
        transform: 'translateX(0) scale(1)',
        opacity: 1,
        zIndex: 3,
        filter: 'blur(0px)'
      };
    } else if (normalizedDiff === 1) {
      return {
        transform: 'translateX(120%) scale(0.75)',
        opacity: 0.25,
        zIndex: 2,
        filter: 'blur(5px)'
      };
    } else if (normalizedDiff === -1) {
      return {
        transform: 'translateX(-120%) scale(0.75)',
        opacity: 0.25,
        zIndex: 2,
        filter: 'blur(5px)'
      };
    } else {
      return {
        transform: 'translateX(0) scale(0.7)',
        opacity: 0,
        zIndex: 1,
        filter: 'blur(6px)'
      };
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleLoginClick = () => {
    window.location.href = '/login';
  };

  return (
    <div className="preptune-redesigned">
      <header className="preptune-header">
        <div className="preptune-header-content">
          <div className="preptune-brand">
            <img src="https://preptune.com/image/logo.png" alt="Preptune Logo" className="preptune-logo" />
            <span>Preptune</span>
          </div>
          <nav className="preptune-nav">
            <a href="https://discord.gg/rxuFJuB9hv" className="preptune-nav-link">Discord</a>
            <button className="preptune-download-btn" onClick={handleLoginClick}>
              Login
            </button>
          </nav>
        </div>
      </header>

      <section className="preptune-hero">
        <h1 className="preptune-hero-title">
          Built for <span className="preptune-hero-italic">you</span>
        </h1>
        <p className="preptune-hero-subtitle">
          Forget less, stress less. Preptune chats, remember what matters and keeps your day organized.
        </p>
        
        <div className="preptune-phone-wrapper">
          <div className="preptune-phone">
            <div className="preptune-phone-notch"></div>
            <div className="preptune-phone-screen">
              <img 
                src="/image/intro-page3.jpg" 
                alt="Preptune Chat" 
                className="preptune-phone-image"
              />
            </div>
          </div>
        </div>

        <p className="preptune-ready-text">Ready to start?</p>
        <button 
          className="preptune-cta-btn"
          onClick={handleLoginClick}
        >
        Sign Up
        </button>
      </section>

      <section className="preptune-unified-section">
        <div className="preptune-what-you-get">
          <h2 className="preptune-wyg-title">What you get</h2>
          
          <div className="preptune-wyg-video-card">
            <div className="preptune-wyg-video-half">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                className="preptune-wyg-video"
              >
                <source src="/videos/intro-vid3.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="preptune-wyg-text-half">
              <h3 className="preptune-wyg-video-title">Introducing: A way to stay on top of your daily routines without any fees.</h3>
            </div>
          </div>

          <div className="preptune-wyg-options">
            <div className="preptune-wyg-option preptune-wyg-option-disabled">
              <span className="preptune-wyg-option-text">AI Companion</span>
              <div className="preptune-wyg-strikethrough"></div>
            </div>
            <div className="preptune-wyg-option preptune-wyg-option-active">
              <span className="preptune-wyg-option-text">AI Courtier</span>
            </div>
          </div>
        </div>

        <div className="preptune-features-container">
          <h2 className="preptune-features-heading">
            More than just chat
          </h2>
          <p className="preptune-features-subheading">
            <span className="preptune-features-highlight">Personalized intelligence</span> with tools that make your day easier
          </p>
          
          <div className="preptune-carousel">
            <button className="preptune-carousel-btn preptune-carousel-prev" onClick={prevSlide}>
              ‹
            </button>
            
            <div className="preptune-carousel-track">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="preptune-carousel-slide"
                  style={getSlideStyle(index)}
                >
                  <div className="preptune-feature-card">
                    <div className="preptune-feature-image-wrapper">
                      <img src={tool.image} alt={tool.title} className="preptune-feature-img" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <button className="preptune-carousel-btn preptune-carousel-next" onClick={nextSlide}>
              ›
            </button>
          </div>

          <div className="preptune-feature-info">
            <h3 className="preptune-feature-title">{tools[activeSlide].title}</h3>
            <p className="preptune-feature-description">{tools[activeSlide].description}</p>
          </div>

          <div className="preptune-carousel-dots">
            {tools.map((_, index) => (
              <button
                key={index}
                className={`preptune-dot ${index === activeSlide ? 'preptune-dot-active' : ''}`}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </div>
        </div>
        <div className="preptune-faq-section">
          <h2 className="preptune-faq-title">Common questions</h2>
          <div className="preptune-faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="preptune-faq-item">
                <button 
                  className="preptune-faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <Plus 
                    className={`preptune-faq-icon ${expandedFaq === index ? 'preptune-faq-icon-expanded' : ''}`}
                    size={20}
                  />
                </button>
                <div className={`preptune-faq-answer ${expandedFaq === index ? 'preptune-faq-answer-expanded' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="preptune-footer">
          <div className="preptune-footer-background">
            <div className="preptune-footer-content">
              <div className="preptune-footer-links-grid">
                <div className="preptune-footer-column">
                  <a href="mailto:support@preptune.com" className="preptune-footer-link">Contact Us</a>
                  <a href="https://discord.gg/rxuFJuB9hv" className="preptune-footer-link">Discord</a>
                  <a href="https://www.instagram.com/preptune?igsh=MXY5Z2c2aDZmeHR1Mg==" className="preptune-footer-social-link" title="Instagram">
                    <Instagram size={18} />
                  </a>
                </div>
                
                <div className="preptune-footer-column">
                  <a href="/privacy" className="preptune-footer-link">Privacy Policy</a>
                  <a href="/terms" className="preptune-footer-link">Terms of Service</a>
                  <a href="/content-dispute" className="preptune-footer-link">Content Dispute Policy</a>
                </div>
              </div>

              <div className="preptune-footer-divider"></div>

              <div className="preptune-footer-bottom">
                <div className="preptune-footer-social"></div>
                <div className="preptune-footer-copyright">
                  © 2026 Preptune. All rights reserved.
                </div>
              </div>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default PreptuneRedesigned;